import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import i18next from 'i18next'
import { Button, Layout, SkeletonPage } from '@shopify/polaris'

import {
  reportsBisProductsQueryOptions,
  reportsBisListQueryOptions,
  reportsBisQueryOptions,
  settingsQueryOptions,
  shopifyGetProductsQueryOptions,
} from '@/common/queryOptions'

import { PageFrame } from '@/components/shared/PageFrame'
import { SkeletonChart } from '@/components/shared/SkeletonChart'
import { SkeletonTable } from '@/components/shared/SkeletonTable'
import { BackInStockStatusBadge } from '@/components/shared/BackInStockStatusBadge'
import { BackInStockChart } from '@/components/backInStock/BackInStockChart'
import { BackInStockProductsTable } from '@/components/backInStock/BackInStockProductsTable'

import { useMutateSettings } from '@/hooks/useMutateSettings'
import { AppEmbedBlock } from '@/components/dashboard/AppEmbedBlock'
import {AskForReviewBanner} from "@/components/shared/AskForReviewBanner.tsx";
import {calculateRevenueTotal, formatPrice} from "@/common/functions.ts";

export const Route = createFileRoute('/back-in-stock/')({
  loader: async ({ context: { queryClient }, route: { path } }) => {
    try {
      // TODO: This may be inefficient - review at some point
      // Start all queries immediately
      const reportsBisProductsPromise = queryClient.ensureQueryData(
        reportsBisProductsQueryOptions({
          page: 1,
        })
      )

      await Promise.all([
        queryClient.ensureQueryData(settingsQueryOptions()),
        queryClient.ensureQueryData(
          reportsBisListQueryOptions({
            from: undefined,
            to: undefined,
          })
        ),
        queryClient.ensureQueryData(
          reportsBisQueryOptions({
            from: undefined,
            to: undefined,
          })
        ),
      ])

      // Wait only for the settings to resolve
      const bisProducts = await reportsBisProductsPromise
      const bisProductIds = bisProducts.data.map((product) => product.productId)

      // Start the dependent query
      queryClient.ensureQueryData(shopifyGetProductsQueryOptions({ productIds: bisProductIds }))
    } catch (error) {
      console.error('Error in page loader.', path, error)
      throw error
    }
  },
  pendingComponent: Pending,
  component: Page,
})

function Page() {
  const navigate = useNavigate()

  const { data: settings } = useSuspenseQuery(settingsQueryOptions())

  const mutateSettings = useMutateSettings()

  const { data: bisReport } = useSuspenseQuery(reportsBisQueryOptions({
    from: undefined,
    to: undefined,
  }))

  const currency = settings?.currency ?? 'USD'
  const dismissFlags = settings?.dismissFlags

  const {orders: mainCurrencyBisOrders, revenue: mainCurrencyBisRevenue} = calculateRevenueTotal(bisReport, currency)
  const totalRevenueFormatted = formatPrice(mainCurrencyBisRevenue, currency)

  return (
    <PageFrame
      title={i18next.t('backInStockDashTitle')}
      subtitle={i18next.t('backInStockDashTitleSubtitle')}
      titleMetadata={<BackInStockStatusBadge enabled={settings.bisEnabled} />}
      primaryAction={
        <Button
          variant="primary"
          disabled={mutateSettings.isPending}
          loading={mutateSettings.isPending}
          onClick={async () => {
            mutateSettings.mutateAsync({
              bisEnabled: !settings.bisEnabled,
            })
          }}
        >
          {settings.bisEnabled ? i18next.t('backInStockCtaUnpublish') : i18next.t('backInStockCtaPublish')}
        </Button>
      }
      secondaryActions={[
        {
          content: i18next.t('genericConfigure'),
          onAction: () => {
            navigate({ to: '/back-in-stock/settings' })
          },
        },
      ]}
    >
      {settings.bisEnabled && <AppEmbedBlock />}
      <Layout.Section>
        <BackInStockChart />
      </Layout.Section>

      {!dismissFlags?.leaveReview && mainCurrencyBisOrders >= 3 && mainCurrencyBisRevenue > 100 &&
        <Layout.Section>
          <AskForReviewBanner
            title={i18next.t('backInStockDashboardLeaveReviewTitle1')}
            description={i18next.t('backInStockDashboardLeaveReviewDescription1', {
              total: totalRevenueFormatted,
            })}
            ctaLabel={i18next.t('backInStockDashboardLeaveReviewCta1')} />
        </Layout.Section>
      }

      <Layout.Section>
        <BackInStockProductsTable />
      </Layout.Section>
    </PageFrame>
  )
}

function Pending() {
  return (
    <SkeletonPage title={i18next.t('backInStockDashTitle')} primaryAction>
      <Layout>
        <Layout.Section>
          <SkeletonChart />
        </Layout.Section>
        <Layout.Section>
          <SkeletonTable />
        </Layout.Section>
      </Layout>
    </SkeletonPage>
  )
}